import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import AuthService from '../services/AuthService';

// Create context for card listing functionality
const ListingContext = createContext({
  listingItems: [],
  addToListing: () => {},
  removeFromListing: () => {},
  updateListingPrice: () => {},
  updateListingCurrency: () => {},
  clearListing: () => {},
  listCards: () => {},
  getTotalListingValue: () => 0,
  marketPrices: {},
  loading: false
});

// Custom hook to easily use the ListingContext
export const useListing = () => useContext(ListingContext);

// Local storage key for persistent listing items
const LISTING_STORAGE_KEY = 'splinterlands_listing_items';

/**
 * Gets current market prices for cards to suggest listing prices
 * @param {Array} cardDetailIds - Array of card detail IDs to get prices for
 * @returns {Promise<Object>} Map of card_detail_id to market price data
 */
const getMarketPrices = async (cardDetailIds) => {
  try {
    // Get current market data for these cards
    const response = await axios.get('https://api.splinterlands.com/market/for_sale_grouped');
    const marketData = Array.isArray(response.data) ? response.data : response.data?.data || [];
    
    const priceMap = {};
    
    // Build price map for the requested cards
    cardDetailIds.forEach(cardDetailId => {
      const marketCards = marketData.filter(card => card.card_detail_id === cardDetailId);
      
      if (marketCards.length > 0) {
        // Get the lowest price for this card type
        const lowestPriceCard = marketCards.reduce((lowest, current) => {
          return (current.low_price || Infinity) < (lowest.low_price || Infinity) ? current : lowest;
        });
        
        priceMap[cardDetailId] = {
          low_price: lowestPriceCard.low_price || 0.01,
          market_count: marketCards.length,
          suggested_price: lowestPriceCard.low_price || 0.01 // Use market low price directly
        };
      } else {
        // Default price if no market data available
        priceMap[cardDetailId] = {
          low_price: 0.01,
          market_count: 0,
          suggested_price: 0.01
        };
      }
    });
    
    return priceMap;
  } catch (error) {
    console.error('Error fetching market prices:', error);
    return {};
  }
};

/**
 * ListingProvider component that provides global state for card listing
 * @param {object} props - React props with children
 * @returns {JSX.Element} - ListingProvider element
 */
export const ListingProvider = ({ children }) => {
  // State for cards selected for listing
  const [listingItems, setListingItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marketPrices, setMarketPrices] = useState({});

  // Load listing items from localStorage on first render
  useEffect(() => {
    const savedItems = localStorage.getItem(LISTING_STORAGE_KEY);
    if (savedItems) {
      try {
        setListingItems(JSON.parse(savedItems));
      } catch (error) {
        console.error('Error loading listing items from localStorage:', error);
        localStorage.removeItem(LISTING_STORAGE_KEY);
      }
    }
  }, []);

  // Save listing items to localStorage on every change
  useEffect(() => {
    localStorage.setItem(LISTING_STORAGE_KEY, JSON.stringify(listingItems));
  }, [listingItems]);

  /**
   * Adds cards to the listing queue
   * @param {Array} cards - Array of card objects to add for listing
   * @param {Object} cardDetails - Card details for name/info
   */
  const addToListing = async (cards, cardDetails) => {
    if (!cards || cards.length === 0) return;

    setLoading(true);

    try {
      // Get unique card detail IDs for market price lookup
      const cardDetailIds = [...new Set(cards.map(card => card.card_detail_id))];
      const prices = await getMarketPrices(cardDetailIds);
      setMarketPrices(prev => ({ ...prev, ...prices }));

      // Create listing items with suggested prices
      const newListingItems = cards.map(card => {
        const cardPrice = prices[card.card_detail_id];
        
        return {
          ...card,
          name: cardDetails?.name || 'Unknown Card',
          rarity: cardDetails?.rarity || card.rarity, // Include rarity from details
          suggested_price: cardPrice?.suggested_price || 0.01,
          listing_price: cardPrice?.suggested_price || 0.01,
          currency: 'USD', // Default to USD
          list_fee: 1, // Default 1% fee
          list_fee_token: 'DEC', // Default fee token
          timeAdded: new Date().toISOString(),
        };
      });

      // Add to listing items, avoiding duplicates
      setListingItems(prevItems => {
        const existingUids = new Set(prevItems.map(item => item.uid));
        const filteredNew = newListingItems.filter(item => !existingUids.has(item.uid));
        return [...prevItems, ...filteredNew];
      });
    } catch (error) {
      console.error('Error adding items to listing:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Removes a card from the listing queue
   * @param {string} uid - The UID of the card to remove
   */
  const removeFromListing = (uid) => {
    setListingItems(prevItems => prevItems.filter(item => item.uid !== uid));
  };

  /**
   * Updates the listing price for a card
   * @param {string} uid - The UID of the card
   * @param {number} price - The new listing price
   */
  const updateListingPrice = (uid, price) => {
    setListingItems(prevItems =>
      prevItems.map(item =>
        item.uid === uid ? { ...item, listing_price: parseFloat(price) } : item
      )
    );
  };

  /**
   * Updates the currency for a card listing
   * @param {string} uid - The UID of the card
   * @param {string} currency - The currency ('USD' or 'DEC')
   */
  const updateListingCurrency = (uid, currency) => {
    setListingItems(prevItems =>
      prevItems.map(item =>
        item.uid === uid ? { ...item, currency } : item
      )
    );
  };

  /**
   * Clears the entire listing queue
   */
  const clearListing = () => {
    setListingItems([]);
  };

  /**
   * Lists all cards in the queue on the marketplace
   */
  const listCards = async () => {
    if (listingItems.length === 0) {
      throw new Error('No cards selected for listing');
    }

    try {
      setLoading(true);

      // Validate if user is logged in
      if (!AuthService.isLoggedIn()) {
        throw new Error('You must be logged in to list cards');
      }

      const username = AuthService.getCurrentUser()?.username;
      if (!username) {
        throw new Error('Unable to get username for listing');
      }

      // Group cards by currency and list_fee to minimize transactions
      const groupedListings = {};
      
      listingItems.forEach(item => {
        const key = `${item.currency}_${item.list_fee}_${item.list_fee_token}`;
        if (!groupedListings[key]) {
          groupedListings[key] = {
            currency: item.currency,
            list_fee: item.list_fee,
            list_fee_token: item.list_fee_token,
            cards: []
          };
        }
        
        groupedListings[key].cards.push({
          uid: item.uid,
          price: item.listing_price
        });
      });

      // Execute listing transactions
      const results = [];
      
      for (const [key, group] of Object.entries(groupedListings)) {
        // Create listing data with multiple card objects in one transaction
        // Each card gets its own object in the array
        const listingData = group.cards.map(card => ({
          cards: [card.uid], // Single card per object
          price: card.price, // Single price per object
          list_fee: group.list_fee,
          currency: group.currency,
          list_fee_token: group.list_fee_token || 'DEC'
        }));

        // Execute the transaction with Hive Keychain
        const result = await new Promise((resolve, reject) => {
          if (!window.hive_keychain) {
            reject(new Error('Hive Keychain not found. Please install the Hive Keychain browser extension.'));
            return;
          }

          window.hive_keychain.requestCustomJson(
            username,
            'sm_sell_cards',
            'Active',
            JSON.stringify(listingData),
            `List ${group.cards.length} card${group.cards.length > 1 ? 's' : ''} for sale`,
            (response) => {
              if (response.success) {
                resolve({
                  success: true,
                  transaction: response,
                  cardsListed: group.cards.length
                });
              } else {
                console.error('Listing transaction failed:', response);
                reject(new Error(response.message || 'Transaction failed. Please try again.'));
              }
            }
          );
        });

        results.push(result);
      }

      // Clear listing items after successful listing
      setListingItems([]);

      return {
        success: true,
        message: `Successfully listed ${listingItems.length} card${listingItems.length > 1 ? 's' : ''} for sale`,
        transactions: results
      };

    } catch (error) {
      console.error('Error listing cards:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Calculate total value of listing items
  const getTotalListingValue = () => {
    return listingItems.reduce((total, item) => total + (item.listing_price || 0), 0);
  };

  // The value provided to the context
  const contextValue = {
    listingItems,
    addToListing,
    removeFromListing,
    updateListingPrice,
    updateListingCurrency,
    clearListing,
    listCards,
    getTotalListingValue,
    marketPrices,
    loading
  };

  return (
    <ListingContext.Provider value={contextValue}>
      {children}
    </ListingContext.Provider>
  );
};

export default ListingContext;
